import { fromPairs, get, isArray, isEmpty, isPlainObject, isString, sortBy } from "lodash";
import type { IProcessOptionalString } from "../process-things";

/** A copied element's text that the target's root language lacked, taken from the source's root language instead. */
export interface TranslationFallback {
  field: string;
  language: string;
  value: string;
}

export interface TranslationLanguages {
  targetLanguages: readonly string[];
  targetRootLanguage: string;
  sourceRootLanguage: string;
}

interface RestrictedStrings {
  strings: IProcessOptionalString[];
  fallback: string | null;
}

// Every translatable text of an element, except the texts of the documents an upload task asks for
const TEXT_FIELDS = [
  "displayName",
  "displayDescription",
  "cancelLabel",
  "finishLabel",
  "reactivatedCancelLabel",
  "reactivatedFinishLabel",
  "attachmentsDisplayText",
] as const;
const DOCUMENT_TEXT_FIELDS = ["displayName", "displayDescription"] as const;
const DOCUMENTS_FIELD = "processDocumentsToUpload";

/**
 * A copy of the element whose texts only keep the target model's languages: the target owns its language list. A text
 * missing in the target's root language takes the source's root-language text, so every copied element has a name
 * the target can show; those fallbacks are returned to be listed.
 */
export function restrictToTargetLanguages<T extends object>(
  node: T,
  languages: TranslationLanguages
): { node: T; fallbacks: TranslationFallback[] } {
  const restrictedFields = TEXT_FIELDS.filter((field) => isArray(get(node, field))).map((field) => ({
    field,
    restricted: restrictStrings(get(node, field), languages),
  }));
  const documents: unknown = get(node, DOCUMENTS_FIELD);
  const restrictedDocuments = isArray(documents)
    ? documents.map((document, index) => restrictDocument(document, index, languages))
    : [];

  const restrictedNode: T = {
    ...node,
    ...fromPairs(restrictedFields.map(({ field, restricted }) => [field, restricted.strings])),
    ...(isArray(documents) ? { [DOCUMENTS_FIELD]: restrictedDocuments.map(({ document }) => document) } : {}),
  };
  const fallbacks = [
    ...restrictedFields
      .filter(({ restricted }) => !isEmpty(restricted.fallback))
      .map(({ field, restricted }) => toFallback(field, restricted.fallback, languages)),
    ...restrictedDocuments.flatMap(({ fallbacks: documentFallbacks }) => documentFallbacks),
  ];
  return { node: restrictedNode, fallbacks };
}

function restrictDocument(
  document: unknown,
  index: number,
  languages: TranslationLanguages
): { document: unknown; fallbacks: TranslationFallback[] } {
  if (!isPlainRecord(document)) {
    return { document, fallbacks: [] };
  }

  const restrictedFields = DOCUMENT_TEXT_FIELDS.filter((field) => isArray(document[field])).map((field) => ({
    field,
    restricted: restrictStrings(get(document, field), languages),
  }));
  return {
    document: {
      ...document,
      ...fromPairs(restrictedFields.map(({ field, restricted }) => [field, restricted.strings])),
    },
    fallbacks: restrictedFields
      .filter(({ restricted }) => !isEmpty(restricted.fallback))
      .map(({ field, restricted }) =>
        toFallback(`${DOCUMENTS_FIELD}.${index}.${field}`, restricted.fallback, languages)
      ),
  };
}

function restrictStrings(value: unknown, languages: TranslationLanguages): RestrictedStrings {
  const { targetLanguages, targetRootLanguage, sourceRootLanguage } = languages;
  const strings = isArray(value) ? value.filter(isProcessString) : [];
  const kept = strings.filter(({ language, value }) => targetLanguages.includes(language) && !isEmpty(value));
  const hasRootLanguage = kept.some(({ language }) => language === targetRootLanguage);
  const sourceRootValue = strings.find(({ language }) => language === sourceRootLanguage)?.value;
  if (hasRootLanguage || isEmpty(sourceRootValue)) {
    return { strings: kept, fallback: null };
  }

  return {
    strings: sortBy([...kept, { language: targetRootLanguage, value: sourceRootValue }], ({ language }) => language),
    fallback: sourceRootValue,
  };
}

function toFallback(field: string, value: string, { targetRootLanguage }: TranslationLanguages): TranslationFallback {
  return { field, language: targetRootLanguage, value };
}

function isProcessString(value: unknown): value is IProcessOptionalString {
  return isPlainRecord(value) && isString(value.language);
}

function isPlainRecord(value: unknown): value is Record<string, unknown> {
  return isPlainObject(value);
}
